import { SerieEntity } from "@arch/domain/entity";
import { SerieMapRepository } from "@arch/infra/repository/map";
import _ from "lodash";

export class CalcMemory {
    private Serie: SerieMapRepository = new SerieMapRepository();

    constructor() {}

    public getExp(serie: SerieEntity): string {
        const expNormalized = this.normalizeExp(serie);
        return expNormalized;
    }

    public normalizeExp(serie: SerieEntity): string {
        const exp = serie.props.exp;
        const compound = serie.props.compound;

        if (!exp || !compound?.length) return serie.props.name;

        const seriesCompound = this.Serie.getByIds(compound);
        if (!seriesCompound?.length) return exp;

        let normalizedExp = exp;

        for (const serie of seriesCompound) {
            if (!this.isValidSerie(serie)) continue;

            normalizedExp = this.processSerieInExpression(normalizedExp, serie);
        }

        return this.cleanExpression(normalizedExp);
    }

    /**
     * Verifica se a série é válida para processamento.
     */
    private isValidSerie(serie: any): boolean {
        return !!(
            serie &&
            serie.props &&
            serie.props._id &&
            serie.props.name &&
            serie.props.sources
        );
    }

    /**
     * Processa uma série na expressão.
     */
    private processSerieInExpression(exp: string, serie: any): string {
        const sources = Array.isArray(serie.props.sources)
            ? serie.props.sources
            : [serie.props.sources].filter(Boolean);

        const hasPreviousMonthPattern = this.hasPreviousMonthInName(
            serie.props.name
        );
        const hasPersonnelOrTaxSource = this.hasPersonnelOrTaxSource(sources);
        const hasPreviousMonthRegex = this.hasPreviousMonthRegexInExp(
            exp,
            serie.props._id
        );

        if (
            hasPersonnelOrTaxSource &&
            !hasPreviousMonthPattern &&
            hasPreviousMonthRegex
        ) {
            return this.replaceWithPreviousMonth(exp, serie);
        } else {
            return this.replaceSerieId(exp, serie.props._id, serie.props.name);
        }
    }

    /**
     * Verifica se o nome da série já contém referência ao mês anterior.
     */
    private hasPreviousMonthInName(name: string): boolean {
        const normalizedName = name.toLowerCase().replace(/\s/g, "");
        return (
            normalizedName.includes("mêsanterior") ||
            normalizedName.includes("mesanterior")
        );
    }

    /**
     * Verifica se a série tem fonte personnel ou tax.
     */
    private hasPersonnelOrTaxSource(sources: string[]): boolean {
        return sources.some(
            (source) => source === "personnel" || source === "tax"
        );
    }

    /**
     * Verifica se a expressão contém padrão de mês anterior para a série.
     */
    private hasPreviousMonthRegexInExp(exp: string, serieId: string): boolean {
        const regex = new RegExp(
            `${this.escapeRegExp(serieId)}\\|\\{'i'\\s*:\\s*-1\\}\\|`,
            "g"
        );
        return regex.test(exp);
    }

    /**
     * Substitui série com padrão de mês anterior.
     */
    private replaceWithPreviousMonth(exp: string, serie: any): string {
        // Primeiro substitui o ID pela nome
        let result = this.replaceSerieId(
            exp,
            serie.props._id,
            serie.props.name
        );

        // Depois substitui o padrão de mês anterior
        const previousMonthRegex = new RegExp(
            `${this.escapeRegExp(serie.props.name)}\\|\\{'i'\\s*:\\s*-1\\}\\|`,
            "g"
        );

        return result.replace(
            previousMonthRegex,
            `${serie.props.name} Mês Anterior`
        );
    }

    /**
     * Substitui ID da série pelo nome.
     */
    private replaceSerieId(
        exp: string,
        serieId: string,
        serieName: string
    ): string {
        const regex = new RegExp(this.escapeRegExp(serieId), "gi");
        return exp.replace(regex, serieName);
    }

    /**
     * Limpa a expressão removendo caracteres desnecessários.
     */
    private cleanExpression(exp: string): string {
        return exp
            .replace(/[\[\]';:'",<>{}]/g, "")
            .replace(/\|\s*i\s+[^|]*\|/g, " Mes Anterior")
            .replace(/\|[^|]*\|/g, "")
            .replace(/abs/gi, "")
            .replace(/negative/gi, "-");
    }

    /**
     * Escapa caracteres especiais para uso em RegExp.
     */
    private escapeRegExp(text: string): string {
        return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }
}
